/**
 * DisplayObject implementations and factory registration.
 *
 * Provides helpers to register factories by view category (3D, 2D, all)
 * so each feature type can declare which DisplayObject handles which views.
 * Note: Volume rendering in 3D is handled directly by VolumeViewport3D
 * via ViewportDataService (not through DisplayFeatureManager).
 */

import type { DisplayObjectRegistry } from '../display-object-registry';
import { DisplayFeatureType, ViewMode } from '../types';
import type { DisplayObjectFactory, IDisplayFeature } from '../types';
import { CenterlinesDisplayObject2D } from './centerlines-display-object-2d';
import { CenterlinesDisplayObject3D } from './centerlines-display-object-3d';
import { CurrentLocationDisplayObject } from './current-location-display-object';
import { SizingDisplayObject } from './sizing-display-object';
import { StructureSetDisplayObject2D } from './structure-set-display-object-2d';
import { StructureSetDisplayObject3D } from './structure-set-display-object-3d';
import { TepOverlayDisplayObject2D } from './tep-overlay-display-object-2d';

// Export DisplayObject classes
export { CenterlinesDisplayObject2D } from './centerlines-display-object-2d';
export { CenterlinesDisplayObject3D } from './centerlines-display-object-3d';
export { CurrentLocationDisplayObject } from './current-location-display-object';
export { SizingDisplayObject } from './sizing-display-object';
export { SphereDisplayObject3D } from './sphere-display-object-3d';
export { StructureSetDisplayObject2D } from './structure-set-display-object-2d';
export { StructureSetDisplayObject3D } from './structure-set-display-object-3d';
export { TepOverlayDisplayObject2D } from './tep-overlay-display-object-2d';
export type { StructureSetFeatureData } from './structure-set-types';

// ============================================================================
// View Mode Groups
// ============================================================================

/** All 3D view modes */
const VIEW_MODES_3D: ViewMode[] = [ViewMode.VIEW_3D];

/** All 2D slice view modes (axial, sagittal, coronal, oblique) */
const VIEW_MODES_2D: ViewMode[] = [
    ViewMode.VIEW_2D_AXIAL,
    ViewMode.VIEW_2D_SAGITTAL,
    ViewMode.VIEW_2D_CORONAL,
    ViewMode.VIEW_2D_OBLIQUE,
];

const VIEW_MODES_ORTHOGONAL: ViewMode[] = [
    ViewMode.VIEW_2D_AXIAL,
    ViewMode.VIEW_2D_SAGITTAL,
    ViewMode.VIEW_2D_CORONAL,
];

/** All CPR view modes */
const VIEW_MODES_CPR: ViewMode[] = [ViewMode.VIEW_CPR_STRETCHED, ViewMode.VIEW_CPR_STRAIGHTENED];

/** All view modes */
const VIEW_MODES_ALL: ViewMode[] = [...VIEW_MODES_3D, ...VIEW_MODES_2D, ...VIEW_MODES_CPR];

// ============================================================================
// Registration Helpers
// ============================================================================

/**
 * Register a factory for a feature type across multiple view modes.
 */
function registerForModes(
    registry: DisplayObjectRegistry,
    featureType: DisplayFeatureType,
    viewModes: ViewMode[],
    factory: DisplayObjectFactory
): void {
    for (const viewMode of viewModes) {
        registry.register(featureType, viewMode, factory);
    }
}

/**
 * Register a factory for all 3D view modes.
 */
function registerFor3D(
    registry: DisplayObjectRegistry,
    featureType: DisplayFeatureType,
    factory: DisplayObjectFactory
): void {
    registerForModes(registry, featureType, VIEW_MODES_3D, factory);
}

/**
 * Register a factory for all 2D slice view modes.
 */
export function registerFor2D(
    registry: DisplayObjectRegistry,
    featureType: DisplayFeatureType,
    factory: DisplayObjectFactory
): void {
    registerForModes(registry, featureType, VIEW_MODES_2D, factory);
}

/**
 * Register a factory for all CPR view modes.
 */
export function registerForCPR(
    registry: DisplayObjectRegistry,
    featureType: DisplayFeatureType,
    factory: DisplayObjectFactory
): void {
    registerForModes(registry, featureType, VIEW_MODES_CPR, factory);
}

/**
 * Register a factory for all view modes.
 */
export function registerForAll(
    registry: DisplayObjectRegistry,
    featureType: DisplayFeatureType,
    factory: DisplayObjectFactory
): void {
    registerForModes(registry, featureType, VIEW_MODES_ALL, factory);
}

// ============================================================================
// Registration
// ============================================================================

/**
 * Register all DisplayObject factories with the registry.
 * Called once during application initialization.
 *
 * @param registry - The DisplayObjectRegistry service
 */
export function registerDisplayObjects(registry: DisplayObjectRegistry): void {
    // --- Centerline ---
    registerFor3D(registry, DisplayFeatureType.CENTERLINE, (feature, viewMode) => {
        return new CenterlinesDisplayObject3D(
            feature as IDisplayFeature<DisplayFeatureType.CENTERLINE>,
            viewMode
        );
    });

    registerForModes(
        registry,
        DisplayFeatureType.CENTERLINE,
        [...VIEW_MODES_ORTHOGONAL],
        (feature, viewMode) => {
            return new CenterlinesDisplayObject2D(
                feature as IDisplayFeature<DisplayFeatureType.CENTERLINE>,
                viewMode
            );
        }
    );

    // --- Current Location (3D sphere + 2D crosshair + CPR horizontal line) ---
    registerForModes(
        registry,
        DisplayFeatureType.CURRENT_LOCATION,
        [...VIEW_MODES_3D, ...VIEW_MODES_ORTHOGONAL, ...VIEW_MODES_CPR],
        (feature, viewMode) => {
            return new CurrentLocationDisplayObject(
                feature as IDisplayFeature<DisplayFeatureType.CURRENT_LOCATION>,
                viewMode
            );
        }
    );

    // --- Sizing (3D + 2D oblique + CPR) ---
    registerForModes(
        registry,
        DisplayFeatureType.SIZING,
        [ViewMode.VIEW_3D, ViewMode.VIEW_2D_OBLIQUE, ...VIEW_MODES_CPR],
        (feature, viewMode) => {
            return new SizingDisplayObject(feature as IDisplayFeature<DisplayFeatureType.SIZING>, viewMode);
        }
    );

    // --- Structure Set (3D + orthogonal 2D) ---
    registerFor3D(registry, DisplayFeatureType.STRUCTURE_SET, (feature, viewMode) => {
        return new StructureSetDisplayObject3D(
            feature as IDisplayFeature<DisplayFeatureType.STRUCTURE_SET>,
            viewMode
        );
    });

    registerForModes(
        registry,
        DisplayFeatureType.STRUCTURE_SET,
        [...VIEW_MODES_ORTHOGONAL],
        (feature, viewMode) => {
            return new StructureSetDisplayObject2D(
                feature as IDisplayFeature<DisplayFeatureType.STRUCTURE_SET>,
                viewMode
            );
        }
    );

    // --- TEP Overlay (orthogonal 2D only) ---
    registerForModes(
        registry,
        DisplayFeatureType.TEP_OVERLAY,
        [...VIEW_MODES_ORTHOGONAL],
        (feature, viewMode) => {
            return new TepOverlayDisplayObject2D(
                feature as IDisplayFeature<DisplayFeatureType.TEP_OVERLAY>,
                viewMode
            );
        }
    );

    // Future registrations:
    // - Keypoint: registerFor3D + registerFor2D
    // - Measurement: registerForAll
}
